import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { combineReducers, Reducer } from 'redux';
import { ReactReduxContext } from 'react-redux';
import {
  logoutCandidateComplete,
  teardownUnifiedLoginSession,
} from '../actions';
import candidateLogin, {
  initialState as candidateLoginInitialState,
} from '../reducers/candidateLogin.reducer';
import portalAuth, {
  initialState as portalAuthInitialState,
} from '../reducers/portalAuth.reducer';
import useIsomorphicLayoutEffect from '../hooks/useIsomorphicLayoutEffect';
import {
  clearCandidateJwtReader,
  setCandidateJwtReader,
} from './authCredentialSource';
import {
  clearAuthLogoutTrigger,
  setAuthLogoutTrigger,
} from './authLogoutTrigger';
import {
  clearAuthContextDispatch,
  setAuthContextDispatch,
} from './authContextMirror';

// ECDEX-3441: the candidateLogin state and the two "auth-relevant portal
// fields" (loggedIn/loggedOut) live here, and only here: MR F removed both from
// the Redux store. The existing, already-tested reducers run via
// combineReducers; candidateLogin.reducer.js and portalAuth.reducer.js are now
// Context reducers (kept in src/reducers until the final Redux teardown).
// Actions still arrive from Redux through authContextMirror.ts, which also
// does the sessionStorage clear the reducers used to do.
//
// candidateLogin.loggedIn is authenticated. portalAuth.loggedIn is a
// successful report load or the non-candidate path. A failed fetch sets
// portal.reportsFetched and leaves portalAuth.loggedIn false, so the two
// stay separate keys rather than one boolean.
const authReducer: Reducer<AuthState> = combineReducers({
  candidateLogin,
  portalAuth,
});

export interface AuthState {
  candidateLogin: typeof candidateLoginInitialState;
  portalAuth: typeof portalAuthInitialState;
}

export interface AuthContextValue {
  state: AuthState;
  dispatch: React.Dispatch<any>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

const authInitialState: AuthState = {
  candidateLogin: candidateLoginInitialState,
  portalAuth: portalAuthInitialState,
};

interface AuthProviderProps {
  children: React.ReactNode;
  // Seeds the Context, for tests that preload Redux state: Context only
  // tracks actions, so preloaded Redux auth state would not reach it.
  initialState?: AuthState;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialState = authInitialState,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // ECDEX-3441 (MR B): receives the auth actions Redux handles (see
  // authContextMirror.ts) so this Context tracks Redux until consumers move.
  // Registration must happen before any child's passive effect runs (e.g.
  // Layout dispatching CHECK_FOR_ACTIVE_AUTH on mount), or that action would
  // be missed. Layout effects of a parent run before children's passive
  // effects; the isomorphic variant avoids useLayoutEffect's SSR/SSG warning,
  // where there is nothing to mirror.
  // Known gap: an auth action dispatched from a child's layout effect or
  // componentDidMount, or before the root mounts, reaches Redux but not this
  // Context, and is not replayed. Nothing does that today.
  useIsomorphicLayoutEffect(() => {
    setAuthContextDispatch(dispatch);
    return () => clearAuthContextDispatch(dispatch);
  }, []);

  // Registers the logout that non-React code (the checkrApiClient /
  // checkrMonolithApiClient 401 interceptors, actions/helper.js's 401 handler)
  // calls through triggerAuthLogout(), so none of them import the Redux store
  // singleton. See authLogoutTrigger.ts.
  //
  // Redux is still the source of truth and also holds the report data that
  // LOGOUT_CANDIDATE resets, so the logout goes to the store when there is one;
  // authContextMirror forwards it to this Context. Dispatching to both here
  // would apply it twice. Without a store (tests, SSG) it goes to the Context
  // directly. Registered in a layout effect, like the mirror, so a 401 that
  // lands before a child's passive effects still logs out.
  const reduxContext = useContext(ReactReduxContext);
  const reduxStore = reduxContext?.store;
  useIsomorphicLayoutEffect(() => {
    const logout = () => {
      // Forced logout, same order as the interactive one: teardown first, as
      // it reads the broker marker out of session storage and the
      // LOGOUT_CANDIDATE reducer clears that storage. It also invalidates
      // in-flight auth attempts and hides Decagon. It does not await the
      // broker, so local state is still cleared synchronously. The plain
      // action, never the logoutCandidate thunk, so it is safe to hand to
      // the Context reducer when there is no store.
      teardownUnifiedLoginSession();
      const action = logoutCandidateComplete();
      if (reduxStore) {
        reduxStore.dispatch(action);
      } else {
        // No store (tests, SSG): the bridge middleware, which clears the
        // session for this action, is not in the path.
        window.sessionStorage.clear();
        dispatch(action);
      }
    };
    setAuthLogoutTrigger(logout);
    return () => clearAuthLogoutTrigger(logout);
  }, [reduxStore]);

  // Exposes the JWT to non-React code (see authCredentialSource.ts). A ref the
  // layout effect keeps current, so a request built from an event handler reads
  // the committed state. Cleared on logout because the reducer empties it.
  const jwtRef = useRef(state.candidateLogin.candidateJwt);
  useIsomorphicLayoutEffect(() => {
    jwtRef.current = state.candidateLogin.candidateJwt;
  }, [state.candidateLogin.candidateJwt]);
  useIsomorphicLayoutEffect(() => {
    const read = () => jwtRef.current || undefined;
    setCandidateJwtReader(read);
    return () => clearCandidateJwtReader(read);
  }, []);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
